import React, { useState, createContext, useContext, ReactNode, useCallback } from 'react';

import { MeModel } from '@/types/users';
import { Status, TeamMemberGlobalContextModel } from '@/types/teams';
import { TeamDetailModel } from '@/types/teams';
import { FeatureFlag } from '@/types/serverinfo';

export interface GlobalValues {
  user: MeModel | undefined;
  user_teams: TeamMemberGlobalContextModel[] | undefined;
  team: TeamDetailModel | undefined;
  featureFlags: FeatureFlag[] | undefined;
  csrftoken: string | undefined;
  systemDate: string | undefined;
  isTeamPage: boolean;
}

export interface GlobalContextProps extends GlobalValues {
  overlapLevel: number;
  hasAppNewVersion: boolean;
  isFeatureEnabled: (featureName: string) => boolean;
  isTeamOwner: () => boolean;
  hasSpecificTeamPlan: (teamUUID: string) => boolean;
  handleSetGlobalValues: (globalValues: GlobalValues | undefined) => void;
  handleIncrementOverlapLevel: () => void;
  handleDecrementOverlapLevel: () => void;
  handleSetHasAppNewVersion: (bool: boolean) => void;
}
const GlobalContext = createContext<GlobalContextProps | undefined>(undefined);

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobal must be used within a GlobalContextProvider');
  }
  return context;
};

export const GlobalContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [globalValues, setGlobalValues] = useState<GlobalValues | undefined>(undefined);
  // NOTE: モーダル、ドロワーコンポーネントの重なりレベルを管理
  const [overlapLevel, setOverlapLevel] = useState<number>(0);
  const [hasAppNewVersion, setHasAppNewVersion] = useState<boolean>(false);

  // handleSetGlobalValuesをuseCallbackで固定化
  const handleSetGlobalValues = React.useCallback(
    (newGlobalValues: GlobalValues | undefined) => {
      setGlobalValues(newGlobalValues);
    },
    [globalValues]
  );

  const handleIncrementOverlapLevel = useCallback(() => {
    setOverlapLevel((prev) => prev + 1);
  }, []);

  const handleDecrementOverlapLevel = useCallback(() => {
    setOverlapLevel((prev) => (prev - 1 < 0 ? 0 : prev - 1));
  }, []);

  // アプリ新バージョンがあるかどうか
  const handleSetHasAppNewVersion = useCallback((bool: boolean) => {
    setHasAppNewVersion(bool);
  }, []);

  // チームオーナーかどうか
  const isTeamOwner = useCallback((): boolean => {
    return globalValues?.user?.id === globalValues?.team?.owner_id;
  }, [globalValues?.user, globalValues?.team]);

  // 特定のチームプランを持っているかどうか
  const hasSpecificTeamPlan = useCallback(
    (teamUUID: string): boolean => {
      if (!globalValues?.user_teams) return false;

      return globalValues.user_teams.some(
        (user_team) => user_team.team.uuid === teamUUID && user_team.status === Status.Approved
      );
    },
    [globalValues?.user_teams]
  );

  // feature flags
  const isFeatureEnabled = useCallback(
    (featureName: string): boolean => {
      return !!globalValues?.featureFlags?.find((flag) => flag.name === featureName)?.is_enabled;
    },
    [globalValues?.featureFlags]
  );

  return (
    <GlobalContext.Provider
      value={{
        user: globalValues?.user,
        user_teams: globalValues?.user_teams,
        team: globalValues?.team,
        featureFlags: globalValues?.featureFlags,
        csrftoken: globalValues?.csrftoken,
        systemDate: globalValues?.systemDate,
        isTeamPage: globalValues?.isTeamPage ?? false,
        overlapLevel,
        hasAppNewVersion,
        isFeatureEnabled,
        isTeamOwner,
        hasSpecificTeamPlan,
        handleSetGlobalValues,
        handleIncrementOverlapLevel,
        handleDecrementOverlapLevel,
        handleSetHasAppNewVersion
      }}>
      {children}
    </GlobalContext.Provider>
  );
};
